/**
 * Markdown Export — LLM-optimized doc exports for Ops dashboard.
 *
 * Every export starts with `buildHavnContext()` so any LLM (Claude Code,
 * Claude in Chrome, etc.) reading the file cold has full context about
 * Havn Club, the menu structure, units, and category vocabulary.
 *
 * Used by ProductionSheet, Plating, BatchCalculator, Recipes, Ordering.
 */

// ══════════════════════════════════════════════════════════════════════════════
// CONTEXT PREAMBLE — sits at the top of every exported .md file
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Build the universal Havn Club context block. Every exported MD file starts
 * with this so LLMs reading the file have all the context they need.
 *
 * @param {object} meta
 * @param {string} meta.docType — e.g. "Production Sheet", "Plating Guide"
 * @param {string} [meta.city] — "dc" | "sd" (lowercase) or display name
 * @param {string} [meta.menu] — menu name, e.g. "May 24 Menu"
 * @param {string} [meta.weekOf] — ISO date for week of delivery
 * @param {string} [meta.docPurpose] — one-line description of what this doc is for
 */
export function buildHavnContext(meta = {}) {
  const cityDisplay = formatCity(meta.city)
  const generatedAt = new Date().toISOString()

  return `<!--
HAVN CLUB — LLM CONTEXT (read first)
═══════════════════════════════════════════════════════════════════════════════

This file is a markdown export from the Havn Club Culinary Ops Dashboard
(ops.havnclub.com). It is designed to be read by an LLM (e.g. Claude Code,
Claude in Chrome) to build follow-on operations, workflows, or analyses.

WHAT IS HAVN CLUB
- Premium meal-prep delivery service operating in Washington DC and San Diego.
- Both cities are operationally identical and roughly equal in size.
- A new menu is published each Tuesday for the FOLLOWING week's delivery.
- Customers order via SMS through Front (helpdesk). Orders tracked in Airtable.
- Deliveries: Sunday Morning, Sunday Evening, or Monday Morning.
- Pricing: meals = $25 each, sides = $10 each. Minimum order = 4 meal-equivalents
  (3 sides = 1 meal-equivalent). Order cutoff: Thursday 4PM ET (grace until 5PM).

WEEKLY MENU STRUCTURE (current default)
- 8 meals + 2 sides + 1 addon.
- Meal slots (internal category names — these are the keys you'll see in data):
    chicken, chicken_2, beef, seafood, seafood_2, veg, cheat, pasta, salad, salad_2
  ("_2" = a second slot of the same protein; same week may run two chickens
   or two seafoods. Each is a distinct dish, customers see dish names not slot
   names. Slot is purely an internal identifier.)
- Side slots: broth (Classic Broth), oats (Chia Pudding — historically "chia").
- Addon: wellness_shots, occasionally date_balls.
- "cheat" is the chef-special / cheat-day slot (filet, smash burger bowl, etc.).
  Customers see this labeled "Special". There is NO separate "chef" category.

CONTAINER TYPES
- Glass — premium reusable container (most customers).
- Plastic — single-use, for new customers / specific request.
- The kitchen plates "standard" Glass + Plastic counts. CUSTOMS are tracked
  separately and plated by hand — they are NOT included in standard counts.

DISH COMPONENT TYPES
- protein, vegetable, carb, sauce, base, seasoning, garnish.
- Each component has a name, portion (in oz), and links to a Recipe.
- Recipes have a base_batch_size (e.g. 30 lbs) and ingredient list.

UNITS USED
- Weight: lbs (default), oz, kg.
- Volume: cups, tbsp, tsp, quarts, liters, ml, gallons.
- Count: each.
- Portion sizes on the plating guide are always in oz.
- Ingredient orders are always in lbs (or display-unit conversion thereof).

ALLERGEN VOCABULARY (Havn-specific — do NOT extrapolate)
- Dairy includes butter, milk, cream, yogurt, cheese, whey, casein, ghee, and other milk-derived ingredients.
- Gluten = wheat grains and products (flour, bread, pasta, orzo, couscous, farro, barley, rye) plus gochujang.
- Soy sauce and tamari are NOT gluten (Havn's soy sauce is gluten-free tamari); they are soy. Worcestershire and oats are NOT gluten.
- Worcestershire IS fish (made with anchovies) unless it is labelled vegan or fish free.
- No nuts are used in any Havn recipe.

DATA INTEGRITY RULES (read before generating any new recipe content)
- This export contains REAL business data. Do NOT invent ingredients, quantities,
  prep instructions, allergens, or any other recipe content.
- If a field is missing, leave it missing — never fabricate values.
- Recipe data flows: Recipes → Dishes (linked via components) → Menus → Orders.

═══════════════════════════════════════════════════════════════════════════════
DOCUMENT METADATA
═══════════════════════════════════════════════════════════════════════════════
- Document type:   ${meta.docType || 'Unknown'}${meta.docPurpose ? `\n- Purpose:         ${meta.docPurpose}` : ''}${cityDisplay ? `\n- City:            ${cityDisplay}` : ''}${meta.menu ? `\n- Menu:            ${meta.menu}` : ''}${meta.weekOf ? `\n- Week of:         ${meta.weekOf}` : ''}
- Generated at:    ${generatedAt}
- Source:          ops.havnclub.com (Culinary Ops Dashboard)
-->

`
}

// ══════════════════════════════════════════════════════════════════════════════
// FORMATTING HELPERS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Build a markdown table. Headers and rows are arrays of strings.
 * Cells are escaped for `|` and newlines.
 */
export function mdTable(headers, rows) {
  if (!headers?.length) return ''
  const esc = (v) => {
    if (v == null) return ''
    return String(v).replace(/\|/g, '\\|').replace(/\n/g, ' ')
  }
  const headerLine = `| ${headers.map(esc).join(' | ')} |`
  const sepLine = `| ${headers.map(() => '---').join(' | ')} |`
  const bodyLines = (rows || []).map(
    (row) => `| ${headers.map((_, i) => esc(row[i] ?? '')).join(' | ')} |`,
  )
  return [headerLine, sepLine, ...bodyLines].join('\n')
}

/** Trigger a download of a markdown file in the browser. */
export function downloadMD(filename, content) {
  const safe = filename.endsWith('.md') ? filename : `${filename}.md`
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = safe
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // Defer revoke so the click handler has flushed first
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

/** Sanitize a string for use in a filename. */
export function slugify(s) {
  if (!s) return 'export'
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/** Format a city id ("dc" | "sd") for display. */
export function formatCity(city) {
  if (!city) return ''
  const c = String(city).toLowerCase()
  if (c === 'dc') return 'Washington DC'
  if (c === 'sd') return 'San Diego'
  return city
}

/** Format a number, trim trailing zeros (3.00 → 3, 6.50 → 6.5). */
export function fmtNum(n, decimals = 2) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  const rounded = Math.round(Number(n) * Math.pow(10, decimals)) / Math.pow(10, decimals)
  return String(rounded)
}

/** Format an ISO date string (or Date) as YYYY-MM-DD. */
export function fmtDate(input) {
  if (!input) return ''
  const d = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

/** Build a today-stamped filename like "production-sheet-dc-2026-05-21.md" */
export function buildFilename(docType, meta = {}) {
  const parts = [slugify(docType)]
  if (meta.city) parts.push(slugify(meta.city))
  if (meta.menu) parts.push(slugify(meta.menu))
  parts.push(fmtDate(new Date()))
  return parts.filter(Boolean).join('-') + '.md'
}
